import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipsModule } from 'primeng/chips';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DataViewModule } from 'primeng/dataview';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AuthService } from '../../../login-service/auth-service';
import { QuizDetails } from '../../../models/quiz';
import { CrudService } from '../../../services/crud-app.service';
import { PageLimitDataShareService } from '../../../services/page-limit-data-share.service';
import { APP_ROUTING } from '../../../app-routing.constants';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-course-quizes',
  standalone: true,
  imports: [
    TooltipModule,
    ToastModule,
    TableModule,
    InputTextModule,
    CardModule,
    TagModule,
    CommonModule,
    FormsModule,
    ChipsModule,
    ButtonModule,
    DataViewModule,
    ConfirmDialogModule,
    DropdownModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './course-quizes.component.html',
  styleUrl: './course-quizes.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CourseQuizesComponent implements OnInit {
  readonly isStudent = this.authService.isStudent;
  @ViewChild('dt2') table!: Table;
  quizzes: QuizDetails[] = [];
  loading: boolean = true;
  selectedQuestion!: QuizDetails;
  roleLoaded: boolean = false;
  @Input() showSelection: boolean = false;
  rowLimit!: number;
  editing: any = false;
  columnGap: number = 0;
  globalSearchQuery: string = '';
  sortOptions: SelectItem[] = [
    { label: 'Name (A-Z)', value: 'nameAsc' },
    { label: 'Name (Z-A)', value: 'nameDesc' },
    { label: 'Creation Date (Newest)', value: 'dateDesc' },
    { label: 'Creation Date (Oldest)', value: 'dateAsc' },
  ];
  selectedSort: string = 'nameAsc';
  submittedQuizzes: Record<string, boolean> = {};
  allQuizzes: QuizDetails[] = [];
  filteredAndSortedQuizzes: QuizDetails[] = [];
  quizAvailability: Record<string, boolean> = {};
  selectedCourseId: string | null = null;

  constructor(
    private crud: CrudService,
    private router: Router,
    private pageLimitDataService: PageLimitDataShareService,
    private readonly confirmationService: ConfirmationService,
    private authService: AuthService,
    private messageService: MessageService,
  ) {}
  ngOnInit() {
    this.roleLoaded = true;
    if (this.authService.getSelectedCourseId()) {
      this.selectedCourseId = this.authService.getSelectedCourseId();
    }

    this.pageLimitDataService.getPageLimit('questionBankComponent').subscribe((rowLimit) => {
      this.rowLimit = rowLimit;
    });
    this.getQuizzes();
  }
  clear(table: Table) {
    table.clear();
  }

  async getQuizzes(): Promise<void> {
    this.loading = true;
    try {
      if (this.selectedCourseId) {
        const endpoint = `quizzes?courseId=${this.selectedCourseId}`;
        const response = await this.crud.get(endpoint);
        const rawQuizzes = <QuizDetails[]>response;
        this.allQuizzes = rawQuizzes.map((quiz) => ({
          ...quiz,
          teacherNamesStr: this.getTeacherNames(quiz.teachers),
          mappedQuizMode: this.getQuizModeLabel(quiz.quizMode),
        }));

        const studentId = this.authService.profileId();

        if (this.authService.isStudent() && studentId) {
          const submittedData = await this.crud.get(
            `quiz-submit/submitted-quizzes?studentId=${studentId}`,
          );

          const submittedQuizIds = new Set(submittedData.submittedQuizIds || []);

          this.allQuizzes.forEach((quiz) => {
            this.submittedQuizzes[quiz.id] = submittedQuizIds.has(quiz.id);
          });
        }
      } else {
        this.allQuizzes = [];
        this.loading = false;
        return;
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      this.loading = false;
      this.cacheQuizAvailability();
      this.applyFilters();
    }
  }
  private cacheQuizAvailability() {
    this.allQuizzes.forEach((quiz) => {
      this.quizAvailability[quiz.id] = this.calculateQuizAvailability(quiz);
    });
  }

  private calculateQuizAvailability(quiz: QuizDetails): boolean {
    if (!quiz.startDate) {
      return false;
    }
    const startDate = new Date(quiz.startDate);
    const currentDate = new Date();
    return currentDate >= startDate;
  }
  get filteredQuizDetails(): QuizDetails[] {
    return this.filteredAndSortedQuizzes;
  }

  applyFilters() {
    const searchLower = this.globalSearchQuery?.toLowerCase().trim();

    let filteredData = [...this.allQuizzes];

    if (this.authService.isStudent()) {
      filteredData = filteredData.filter((quiz) => quiz.isPublish === true);
    }

    if (searchLower) {
      filteredData = filteredData.filter((quiz) => {
        const name = quiz.name?.toLowerCase() || '';
        const subject = quiz.subject?.toLowerCase() || '';
        const quizModeMapped = quiz.mappedQuizMode?.toLowerCase() || '';
        const quizModeRaw = quiz.quizMode?.toLowerCase() || '';
        const teacherNames = quiz.teacherNamesStr?.toLowerCase() || '';
        const totalQuestions = quiz.totalQuestions?.toString() || '';

        return (
          name.includes(searchLower) ||
          subject.includes(searchLower) ||
          quizModeMapped.includes(searchLower) ||
          quizModeRaw.includes(searchLower) ||
          teacherNames.includes(searchLower) ||
          totalQuestions.includes(searchLower)
        );
      });
    }

    filteredData.sort((a, b) => {
      switch (this.selectedSort) {
        case 'nameAsc':
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
        case 'nameDesc':
          return b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' });
        case 'dateDesc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'dateAsc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });

    this.filteredAndSortedQuizzes = filteredData;
  }

  onFilterChange() {
    this.applyFilters();
  }

  getQuizModeLabel(mode: string): string {
    const modes: Record<string, string> = {
      flash: 'Flash Feedback',
      flashpro: 'Flash Pro',
      review: 'Review & Reveal',
      timeChallenged: 'Timed Challenge',
      timechallenged: 'Timed Challenge',
    };
    return modes[mode] || modes[mode?.toLowerCase()] || mode || '';
  }

  getTeacherNames(teachers: any[]): string {
    if (!teachers || teachers.length === 0) {
      return 'No teachers assigned';
    }
    return teachers.map((teacher) => teacher.firstName + ' ' + teacher.lastName).join(', ');
  }

  checkQuizDeadline(date: string): boolean {
    let currentDate = new Date();
    let deadlineDate = new Date(date);
    return currentDate > deadlineDate;
  }
  isQuizAvailable(quiz: any): boolean {
    return this.quizAvailability[quiz.id] ?? this.calculateQuizAvailability(quiz);
  }
  getTimeUntilQuizStarts(quiz: any): string {
    if (!quiz.startDate) {
      return 'Publish date not set';
    }
    const startDate = new Date(quiz.startDate);
    const currentDate = new Date();
    if (currentDate >= startDate) {
      return '';
    }
    const timeDiff = startDate.getTime() - currentDate.getTime();
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} day(s), ${hours} hour(s)`;
    } else if (hours > 0) {
      return `${hours} hour(s), ${minutes} minute(s)`;
    } else {
      return `${minutes} minute(s)`;
    }
  }

  formatStartDate(startDate: string): string {
    if (!startDate) {
      return 'Not set';
    }
    const date = new Date(startDate);
    return date.toLocaleString();
  }

  isQuizDisabled(quiz: any): boolean {
    if (this.submittedQuizzes[quiz.id]) {
      return true;
    }

    if (!this.isQuizAvailable(quiz)) {
      return true;
    }

    if (quiz.deadlineDate && this.checkQuizDeadline(quiz.deadlineDate)) {
      return true;
    }

    return false;
  }

  getQuizButtonLabel(quiz: any): string {
    if (this.submittedQuizzes[quiz.id]) {
      return 'Submitted';
    }

    if (!this.isQuizAvailable(quiz)) {
      return 'Yet to Start';
    }

    return 'Take Quiz';
  }

  getQuizButtonTooltip(quiz: any): string {
    if (this.submittedQuizzes[quiz.id]) {
      return 'You have already submitted this quiz';
    }

    if (quiz.deadlineDate && this.checkQuizDeadline(quiz.deadlineDate)) {
      return `Quiz deadline has passed (${this.formatStartDate(quiz.deadlineDate)})`;
    }

    if (!this.isQuizAvailable(quiz)) {
      const startDate = this.formatStartDate(quiz.startDate);
      const timeRemaining = this.getTimeUntilQuizStarts(quiz);
      return `Quiz starts at ${startDate}${timeRemaining ? '. Time remaining: ' + timeRemaining : ''}`;
    }

    return 'Click to take this quiz';
  }

  getQuizButtonSeverity(
    quiz: any,
  ): 'success' | 'info' | 'warning' | 'danger' | 'help' | 'primary' | 'secondary' | 'contrast' {
    if (this.submittedQuizzes[quiz.id]) {
      return 'success';
    }

    if (!this.isQuizAvailable(quiz)) {
      return 'warning';
    }

    return 'info';
  }

  onTakeQuiz(quiz: any) {
    if (this.submittedQuizzes[quiz.id]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Already Submitted',
        detail: 'You have already submitted this quiz.',
        life: 3000,
      });
      return;
    }

    // Check if quiz is available (start time)
    if (this.authService.isStudent()) {
      const isAvailable = this.isQuizAvailable(quiz);
      if (!isAvailable) {
        const startDate = this.formatStartDate(quiz.startDate);
        const timeRemaining = this.getTimeUntilQuizStarts(quiz);

        this.messageService.add({
          severity: 'info',
          summary: 'Quiz Not Available Yet',
          detail: `Quiz will be available at ${startDate}. Please check back later. Time remaining: ${timeRemaining}`,
          life: 6000,
        });
        return;
      }
    }

    // Navigate to quiz based on mode
    const quizMode = quiz.quizMode.toLowerCase();

    switch (quizMode) {
      case 'flashpro':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'flashpro']);
        break;
      case 'flash':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'flash']);
        break;
      case 'review':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'review']);
        break;
      case 'timechallenged':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'timeChallenged']);
        break;
      default:
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'mode']);
        break;
    }
  }
  getAvailabilityIcon(quiz: any): string {
    if (this.submittedQuizzes[quiz.id]) {
      return 'pi pi-check-circle';
    }

    if (!this.isQuizAvailable(quiz)) {
      return 'pi pi-clock';
    }

    return 'pi pi-play';
  }

  onViewDashboard(quiz: any) {
    if (!quiz.isPublish) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Available',
        detail: 'Dashboard is only available for published quizzes.',
        life: 3000,
      });
      return;
    }

    // Check if student has submitted the quiz (allow expired quizzes through)
    const isExpired = quiz.deadlineDate && this.checkQuizDeadline(quiz.deadlineDate);
    if (this.authService.isStudent() && !this.submittedQuizzes[quiz.id] && !isExpired) {
      this.messageService.add({
        severity: 'info',
        summary: 'Not Available',
        detail: 'Please submit the quiz first to view your analysis.',
        life: 3000,
      });
      return;
    }

    // Navigate to the appropriate quiz mode with analysis view mode
    const quizMode = quiz.quizMode.toLowerCase();
    const studentId = this.authService.profileId();

    switch (quizMode) {
      case 'flashpro':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'flashpro'], {
          queryParams: {
            viewMode: 'analysis',
            studentId: studentId,
          },
        });
        break;
      case 'flash':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'flash'], {
          queryParams: {
            viewMode: 'analysis',
            studentId: studentId,
          },
        });
        break;
      case 'review':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'review'], {
          queryParams: {
            viewMode: 'analysis',
            studentId: studentId,
          },
        });
        break;
      case 'timechallenged':
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'timeChallenged'], {
          queryParams: {
            viewMode: 'analysis',
            studentId: studentId,
          },
        });
        break;
      default:
        this.router.navigate([APP_ROUTING['TAKE_QUIZ'], quiz.id, 'mode'], {
          queryParams: {
            viewMode: 'analysis',
            studentId: studentId,
          },
        });
        break;
    }
  }
  onViewAnalytics(quizId: string): void {
    this.router.navigate(['/pages/quiz-analysis', quizId]);
  }
  createNew() {
    this.router.navigate(['pages/quiz/quiz-app', 0]);
  }

  onRowdelete(event: Event, quizData: any) {
    if (quizData.isPublish) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Delete',
        detail: 'Quiz cannot be deleted once it is published.',
        life: 3000,
      });
      return;
    }

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Do you want to delete "${quizData.name}" quiz?`,
      header: 'Delete Quiz Confirmation',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        try {
          await this.crud.delete(`quizzes/${quizData.id}`);

          this.allQuizzes = this.allQuizzes.filter((quiz) => quiz.id !== quizData.id);

          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `Quiz "${quizData.name}" has been deleted successfully.`,
            life: 3000,
          });

          this.applyFilters();
        } catch (error) {
          console.error('Error deleting quiz:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete quiz. Please try again.',
            life: 3000,
          });
        }
      },
    });
  }

  onGlobalFilter(event: Event) {
    const input = event.target as HTMLInputElement;
    this.globalSearchQuery = input.value;
    this.applyFilters();
  }

  customSort(event: any) {
    event.data.sort((a: any, b: any) => {
      const valA = a[event.field] ?? '';
      const valB = b[event.field] ?? '';
      const result = String(valA).localeCompare(String(valB), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return event.order * result;
    });
  }

  onRowLimitChange = (limit: number) => {
    this.pageLimitDataService.setPageLimit('questionBankComponent', limit);
  };

  EditCourse(id: number) {
    this.router.navigate(['pages/quiz/quiz-app', id]);
  }
}
